import { Box, Radio, Stack, Typography } from '@mui/material';
import ChipLabel from '/@/components/chip-label';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import {
    CYCLE_LABELS,
    type Hybrid,
    PRECEON_BRAND_CODENAME,
    PRECEON_GRADIENT,
} from '/@/views/recommendations/v2/steps/hybrids/hybrid-card';

/** Width of the vertical brand ribbon, and of the gap reserved for it on rows without one. */
const BRAND_RIBBON_WIDTH = 24;

/**
 * One selectable product, as a row. The step sizes the column it sits in.
 *
 * Deliberately not `HybridCard`: that one is a grid tile built around the defensive profile and
 * the ranking badge, neither of which this step shows. What the two do share — the Preceon
 * treatment, the cycle chip, the estimated-yield formatting — is imported rather than copied.
 */
function ProductCard({
    hybrid,
    selected,
    onSelect,
    reserveBrandRibbon,
}: {
    hybrid: Hybrid;
    selected: boolean;
    onSelect: () => void;
    /**
     * Reserves the ribbon's width on rows that don't have one, so a list containing a Preceon
     * hybrid keeps every row's radio and name on the same left edge instead of stepping in and
     * out by 24px. The step sets it once for the whole list rather than per row.
     */
    reserveBrandRibbon?: boolean;
}) {
    const units = useUnitProvider();
    const isPreceon = hybrid.brand?.codename === PRECEON_BRAND_CODENAME;
    const cycleLabel = hybrid.cycle ? CYCLE_LABELS[hybrid.cycle]?.() : undefined;

    // Catalogue hybrids carry no estimated yield — the API ranks only the featured ones — so the
    // whole block drops out rather than rendering a blank or a zero.
    const estimatedYield = hybrid.recommended?.yield;
    const yieldValue = estimatedYield ? units.toYield(estimatedYield, hybrid.brand?.type || 'corn', 0) : undefined;
    const hasYield = !!yieldValue && yieldValue.localeValue !== 'NaN';

    return (
        <Stack
            direction='row'
            onClick={onSelect}
            data-testid={`product-card-${hybrid.id}`}
            sx={{
                position: 'relative',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: (theme) => (selected ? theme.palette.primary.onSurface : theme.palette.divider),
                // The selected row is filled as well as ringed — the radio alone is too small to
                // carry the state at this row height.
                backgroundColor: (theme) => (selected ? theme.palette.grey[100] : theme.palette.background.paper),
                cursor: 'pointer',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: '-1px',
                    borderRadius: 'inherit',
                    border: '2px solid',
                    borderColor: (theme) => (selected ? theme.palette.primary.onSurface : 'transparent'),
                    pointerEvents: 'none',
                },
            }}
        >
            {isPreceon ? (
                // Vertical text has no intrinsic width as a flex child, so the ribbon is sized
                // explicitly rather than left to shrink to nothing.
                <Stack
                    alignItems='center'
                    justifyContent='center'
                    sx={{ width: BRAND_RIBBON_WIDTH, flexShrink: 0, background: PRECEON_GRADIENT }}
                >
                    <Typography
                        variant='caption'
                        fontWeight='bold'
                        color='common.white'
                        textTransform='uppercase'
                        noWrap
                        sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: '0.08em' }}
                    >
                        {_('Preceon')}
                    </Typography>
                </Stack>
            ) : (
                reserveBrandRibbon && <Box aria-hidden sx={{ width: BRAND_RIBBON_WIDTH, flexShrink: 0 }} />
            )}

            <Stack
                direction='row'
                alignItems='center'
                spacing={{ xs: 2, sm: 5 }}
                px={{ xs: 2, sm: 3 }}
                py={2}
                flexGrow={1}
                sx={{ minWidth: 0 }}
                useFlexGap
            >
                <Radio checked={selected} sx={{ p: 0, flexShrink: 0 }} />

                {/* The yield sits beside the name on a wide row and wraps under it on a phone,
                    where a right-aligned column would squeeze the hybrid's name to nothing. */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent='space-between'
                    spacing={{ xs: 2, sm: 4 }}
                    sx={{ minWidth: 0, flexGrow: 1 }}
                    useFlexGap
                >
                    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                        <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                            {hybrid.catalogName}
                        </Typography>
                        {cycleLabel && (
                            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                                <ChipLabel label={cycleLabel} type='cycle' size='sm' />
                            </Stack>
                        )}
                    </Stack>

                    {hasYield && (
                        <Stack sx={{ flexShrink: 0 }}>
                            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                {yieldValue.localeValue} {yieldValue.unit}
                            </Typography>
                            <Typography variant='caption' color='primary.mui.inactive'>
                                {_('Rendimiento estimado')}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
}

export default ProductCard;
